import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FiFilter, FiSearch, FiX, FiEye, FiEdit2, FiDownload, FiBell, FiPaperclip, FiExternalLink, FiClock, FiTruck, FiCheckCircle, FiXCircle, FiShoppingBag } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { api } from '../../api/api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar } from 'react-icons/fi';

const statusOptions = [
  'pending',
  'processing',
  'shipped',
  'completed',
  'cancelled'
];

const Orders = () => {
  // Define all hooks at the top level
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const applyFilters = () => {
    let result = [...orders];

    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(order => order.status === filterStatus);
    }

    // Apply date range filter
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      result = result.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= start && orderDate <= end;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(order => 
        order.order_number.toLowerCase().includes(searchLower) ||
        order.shipping_address.first_name.toLowerCase().includes(searchLower) ||
        order.shipping_address.last_name.toLowerCase().includes(searchLower) ||
        order.shipping_address.email.toLowerCase().includes(searchLower)
      );
    }

    setFilteredOrders(result);
    setShowFilterModal(false);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setDateRange({ startDate: '', endDate: '' });
    setFilteredOrders(orders);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      // First, try using getAllOrders
      let data = await api.getAllOrders().catch(async () => {
        // If that fails, try using getOrders as fallback
        return await api.getOrders();
      });

      console.log('Fetched orders data:', data); // Debug log
      
      // Ensure we always have an array
      const ordersArray = Array.isArray(data) ? data : (data?.results || []);
      
      setOrders(ordersArray);
      setFilteredOrders(ordersArray);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to fetch orders');
      toast.error('Failed to load orders. Please try again later.');
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderUpdate = (orderNumber, updatedData) => {
    const updateOrders = (prevOrders) =>
      prevOrders.map(order =>
        order.order_number === orderNumber
          ? { ...order, ...updatedData }
          : order
      );

    setOrders(updateOrders);
    setFilteredOrders(updateOrders);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await api.updateOrderStatus(orderId, newStatus);
      
      // Update local state
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      );
      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);

      toast.success('Order status updated successfully');
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const getExportData = () => {
    return filteredOrders.map(order => ({
      'Order Number': order.order_number,
      'Customer Name': `${order.shipping_address.first_name} ${order.shipping_address.last_name}`,
      'Customer Email': order.shipping_address.email,
      'Customer Phone': order.shipping_address.phone,
      'Total Amount': `₹${order.total_amount}`,
      'Status': order.status,
      'Payment Status': order.payment_status,
      'Order Date': new Date(order.created_at).toLocaleDateString(),
      'Shipping Address': `${order.shipping_address.street_address_1}, ${order.shipping_address.street_address_2}, ${order.shipping_address.city}, ${order.shipping_address.state} - ${order.shipping_address.pincode}`,
      'Razorpay Order ID': order.razorpay_order_id,
      'Razorpay Payment ID': order.razorpay_payment_id
    }));
  };

  const generateInvoice = (order) => {
    const doc = generateInvoicePDF(order);
    doc.save(`invoice-${order.invoiceNumber}.pdf`);
  };

  const exportOrdersPDF = () => {
    const doc = generateOrdersExportPDF(filteredOrders);
    doc.save(`orders-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const getOrderPriorityColor = (order) => {
    const orderDate = new Date(order.createdAt);
    const hoursSinceOrder = (new Date() - orderDate) / (1000 * 60 * 60);
    
    if (order.status === 'pending' && hoursSinceOrder < 24) {
      return 'border-l-4 border-red-500 bg-red-50';
    } else if (order.status === 'pending' || order.status === 'processing') {
      return 'border-l-4 border-yellow-500 bg-yellow-50';
    }
    return '';
  };

  const sortOrdersByPriority = (orders) => {
    return [...orders].sort((a, b) => {
      // First, sort by payment status (completed first, then pending)
      const paymentA = a.payment_status?.toLowerCase();
      const paymentB = b.payment_status?.toLowerCase();
      
      if (paymentA === 'completed' && paymentB !== 'completed') return -1;
      if (paymentA !== 'completed' && paymentB === 'completed') return 1;
      if (paymentA === 'pending' && paymentB !== 'pending' && paymentB !== 'completed') return -1;
      if (paymentA !== 'pending' && paymentB === 'pending' && paymentA !== 'completed') return 1;

      // Then, sort by order status
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;
      
      // If both have same payment status and order status, sort by date (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  useEffect(() => {
    let result = [...orders];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(order => 
        order.order_number?.toLowerCase().includes(searchLower) ||
        order.shipping_address?.first_name?.toLowerCase().includes(searchLower) ||
        order.shipping_address?.last_name?.toLowerCase().includes(searchLower) ||
        order.shipping_address?.email?.toLowerCase().includes(searchLower) ||
        order.shipping_address?.phone?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filterStatus && filterStatus !== 'all') {
      result = result.filter(order => order.status === filterStatus);
    }

    // Apply date range filter
    if (dateRange.startDate && dateRange.endDate) {
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date

      result = result.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    // Always sort by priority (pending first)
    result = sortOrdersByPriority(result);
    
    setFilteredOrders(result);
  }, [orders, searchTerm, filterStatus, dateRange]);

  // Calculate pagination values
  const sortedOrders = sortOrdersByPriority(filteredOrders);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);

  // Add loading state handling
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Orders | Admin Dashboard</title>
      </Helmet>

      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header with Stats */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4">Orders Management</h1>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 sm:gap-4">
            {/* New Orders */}
            <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">New Orders</p>
                  <p className="text-xl sm:text-2xl font-semibold text-red-600">
                    {orders.filter(o => o.status === 'pending').length}
                  </p>
                </div>
                <div className="bg-red-100 p-2 sm:p-3 rounded-full">
                  <FiBell className="h-4 w-4 sm:h-6 sm:w-6 text-red-600" />
                </div>
              </div>
            </div>

            {/* Processing Orders */}
            <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Processing</p>
                  <p className="text-xl sm:text-2xl font-semibold text-blue-600">
                    {orders.filter(o => o.status === 'processing').length}
                  </p>
                </div>
                <div className="bg-blue-100 p-2 sm:p-3 rounded-full">
                  <FiClock className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
                </div>
              </div>
            </div>

            {/* Shipped Orders */}
            <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Shipped</p>
                  <p className="text-xl sm:text-2xl font-semibold text-purple-600">
                    {orders.filter(o => o.status === 'shipped').length}
                  </p>
                </div>
                <div className="bg-purple-100 p-2 sm:p-3 rounded-full">
                  <FiTruck className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
                </div>
              </div>
            </div>

            {/* Delivered Orders */}
            <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Delivered</p>
                  <p className="text-xl sm:text-2xl font-semibold text-green-600">
                    {orders.filter(o => o.status === 'completed').length}
                  </p>
                </div>
                <div className="bg-green-100 p-2 sm:p-3 rounded-full">
                  <FiCheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* Cancelled Orders */}
            <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Cancelled</p>
                  <p className="text-xl sm:text-2xl font-semibold text-red-600">
                    {orders.filter(o => o.status === 'cancelled').length}
                  </p>
                </div>
                <div className="bg-red-100 p-2 sm:p-3 rounded-full">
                  <FiXCircle className="h-4 w-4 sm:h-6 sm:w-6 text-red-600" />
                </div>
              </div>
            </div>

            {/* Total Orders */}
            <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">Total Orders</p>
                  <p className="text-xl sm:text-2xl font-semibold text-gray-800">
                    {orders.length}
                  </p>
                </div>
                <div className="bg-gray-100 p-2 sm:p-3 rounded-full">
                  <FiShoppingBag className="h-4 w-4 sm:h-6 sm:w-6 text-gray-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm mb-4 sm:mb-6 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => setShowFilterModal(true)}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FiFilter className="mr-2 -ml-1 h-4 w-4 sm:h-5 sm:w-5" />
                Filters
              </button>
            </div>
          </div>
        </div>

        {/* Orders List View */}
        <div className="space-y-4">
          {currentOrders?.map((order) => (
            <motion.div
              key={order?.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={`bg-white rounded-xl shadow-sm ${getOrderPriorityColor(order)}`}
            >
              <div className="p-4">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Order ID and Date */}
                  <div className="min-w-[200px]">
                    <span className="text-lg font-medium text-gray-900">#{order?.order_number}</span>
                    <div className="text-sm text-gray-500">
                      {new Date(order?.created_at).toLocaleDateString()} at{' '}
                      {new Date(order?.created_at).toLocaleTimeString()}
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="flex items-center flex-1">
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xl font-medium text-gray-600">
                        {order?.shipping_address?.first_name?.charAt(0)}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {`${order?.shipping_address?.first_name} ${order?.shipping_address?.last_name}`}
                      </div>
                      <div className="text-sm text-gray-500">{order?.shipping_address?.email}</div>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <span className="text-sm text-gray-500 block">Items</span>
                      <span className="text-sm font-medium">{order?.items?.length}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-sm text-gray-500 block">Amount</span>
                      <span className="text-sm font-medium">₹{order?.total_amount}</span>
                    </div>
                    <div className="text-center">
                      <span className="text-sm text-gray-500 block">Payment</span>
                      <span className={`text-xs font-medium ${getPaymentStatusColor(order?.payment_status)}`}>
                        {order?.payment_status?.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(order?.status)}`}>
                      {order?.status?.toUpperCase()}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="p-2 text-indigo-600 hover:text-indigo-900 bg-indigo-50 rounded-full"
                      >
                        <FiEye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Previous Page Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-sm ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } transition-colors`}
              >
                Prev
              </motion.button>

              {/* Page Numbers - Show fewer on mobile */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  // On mobile, show fewer page numbers
                  if (window.innerWidth < 640) {
                    return page === 1 || page === totalPages || 
                           (page >= currentPage - 1 && page <= currentPage + 1);
                  }
                  return true;
                })
                .map((page) => (
                  <motion.button
                    key={page}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-sm ${
                      currentPage === page
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    {page}
                  </motion.button>
                ))}

              {/* Next Page Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-sm ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } transition-colors`}
              >
                Next
              </motion.button>
            </div>
          </div>
        )}

        {/* Show page info */}
        <div className="text-center mt-4 text-xs sm:text-sm text-gray-500">
          Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredOrders.length)} of {filteredOrders.length} orders
        </div>
      </div>

      {/* Filter Modal - Make responsive */}
      {showFilterModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full w-full max-w-md mx-auto">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Filter Orders
                      </h3>
                      <button
                        onClick={() => setShowFilterModal(false)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <FiX className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <select
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        >
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Date Range
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="relative datepicker-icon-container">
                            <FiCalendar className="datepicker-icon" />
                            <DatePicker
                              selected={dateRange.startDate ? new Date(dateRange.startDate) : null}
                              onChange={(date) => setDateRange({ ...dateRange, startDate: date.toISOString() })}
                              selectsStart
                              startDate={dateRange.startDate ? new Date(dateRange.startDate) : null}
                              endDate={dateRange.endDate ? new Date(dateRange.endDate) : null}
                              dateFormat="MMM dd, yyyy"
                              placeholderText="Start Date"
                              wrapperClassName="w-full"
                              calendarClassName="shadow-lg"
                              showPopperArrow={false}
                              autoComplete="off"
                            />
                          </div>
                          <div className="relative datepicker-icon-container">
                            <FiCalendar className="datepicker-icon" />
                            <DatePicker
                              selected={dateRange.endDate ? new Date(dateRange.endDate) : null}
                              onChange={(date) => setDateRange({ ...dateRange, endDate: date.toISOString() })}
                              selectsEnd
                              startDate={dateRange.startDate ? new Date(dateRange.startDate) : null}
                              endDate={dateRange.endDate ? new Date(dateRange.endDate) : null}
                              minDate={dateRange.startDate ? new Date(dateRange.startDate) : null}
                              dateFormat="MMM dd, yyyy"
                              placeholderText="End Date"
                              wrapperClassName="w-full"
                              calendarClassName="shadow-lg"
                              showPopperArrow={false}
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 flex flex-col sm:flex-row-reverse gap-2">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="w-full inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Apply Filters
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="w-full inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal - Make responsive */}
      {showOrderModal && selectedOrder && (
        <OrderDetailsModal 
          selectedOrder={selectedOrder}
          setShowOrderModal={setShowOrderModal}
          getStatusColor={getStatusColor}
          getPaymentStatusColor={getPaymentStatusColor}
          onOrderUpdate={handleOrderUpdate}
        />
      )}
      {showInvoiceModal && selectedInvoice && (
        <InvoiceModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}
        />
      )}
    </>
  );
};

const OrderDetailsModal = ({ selectedOrder, setShowOrderModal, getStatusColor, getPaymentStatusColor, onOrderUpdate }) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState({
    status: selectedOrder.status,
    tracking_link: selectedOrder.tracking_link || '',
    invoice: null
  });

  const validateTrackingLink = (link) => {
    if (!link) return true; // Empty link is valid
    try {
      new URL(link);
      return true;
    } catch {
      return false;
    }
  };

  const isPaymentPending = selectedOrder.payment_status?.toLowerCase() === 'pending';

  const handleStatusUpdate = async () => {
    if (isPaymentPending) {
      toast.error('Cannot update order status while payment is pending');
      return;
    }

    if (trackingInfo.status === 'shipped' && 
        trackingInfo.tracking_link && 
        !validateTrackingLink(trackingInfo.tracking_link.trim())) {
      toast.error('Please enter a valid tracking link');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('status', trackingInfo.status);
      
      if (trackingInfo.tracking_link.trim()) {
        formData.append('tracking_link', trackingInfo.tracking_link.trim());
      }
      
      if (trackingInfo.invoice) {
        formData.append('invoice', trackingInfo.invoice);
      }

      const updatedOrder = await api.updateOrderStatus(selectedOrder.order_number, formData);
      
      if (onOrderUpdate) {
        onOrderUpdate(selectedOrder.order_number, updatedOrder);
      }
      
      toast.success('Order status updated successfully');
      setShowOrderModal(false);
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(error.message || 'Failed to update order status');
    }
  };

  // Calculate subtotal from items
  const subtotal = selectedOrder.items.reduce((sum, item) => 
    sum + parseFloat(item.total), 0
  ).toFixed(2);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header with Customer Info, Order Number and Close Button */}
          <div className="flex justify-between items-start mb-6 border-b pb-4">
            <div>
              <div className="flex items-center mb-3">
                <div className="flex-shrink-0 h-8 w-8">
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600">
                      {selectedOrder.shipping_address.first_name.charAt(0)}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">
                    {`${selectedOrder.shipping_address.first_name} ${selectedOrder.shipping_address.last_name}`}
                  </div>
                  <div className="text-xs text-gray-500">{selectedOrder.shipping_address.phone}</div>
                </div>
              </div>
              <h2 className="text-xl font-semibold">Order #{selectedOrder.order_number}</h2>
              <p className="text-sm text-gray-500">
                Placed on {new Date(selectedOrder.created_at).toLocaleString()}
              </p>
            </div>
            <button 
              onClick={() => setShowOrderModal(false)} 
              className="text-gray-500 hover:text-gray-700"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          {/* Add Status Update Section */}
          <div className="mb-6 border-b pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Update Order Status</h3>
              <button 
                onClick={() => setIsUpdatingStatus(!isUpdatingStatus)}
                className="text-indigo-600 hover:text-indigo-800"
              >
                {isUpdatingStatus ? 'Cancel' : 'Update Status'}
              </button>
            </div>

            {isUpdatingStatus && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={trackingInfo.status}
                    onChange={(e) => setTrackingInfo({
                      ...trackingInfo,
                      status: e.target.value
                    })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {trackingInfo.status === 'shipped' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tracking Link
                      </label>
                      <input
                        type="text"
                        value={trackingInfo.tracking_link}
                        onChange={(e) => setTrackingInfo({
                          ...trackingInfo,
                          tracking_link: e.target.value
                        })}
                        placeholder="Enter tracking link"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoice
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                          onChange={(e) => setTrackingInfo({
                            ...trackingInfo,
                            invoice: e.target.files[0]
                          })}
                          className="block w-full text-sm text-gray-500
                            file:mr-4 file:py-2 file:px-4
                            file:rounded-md file:border-0
                            file:text-sm file:font-medium
                            file:bg-indigo-50 file:text-indigo-700
                            hover:file:bg-indigo-100"
                        />
                        {trackingInfo.invoice && (
                          <span className="text-sm text-gray-500">
                            {trackingInfo.invoice.name}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Accepted formats: PDF, DOC, DOCX, JPG, PNG, WebP
                      </p>
                    </div>
                  </>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={handleStatusUpdate}
                    disabled={isPaymentPending}
                    className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      isPaymentPending 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {isPaymentPending ? 'Payment Pending' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Status and Payment Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Order Status</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(selectedOrder.status)}`}>
                      {selectedOrder.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Payment Status</span>
                    <span className={`px-3 py-1 rounded-full text-xs ${getPaymentStatusColor(selectedOrder.payment_status)}`}>
                      {selectedOrder.payment_status.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Amount</span>
                    <span className="font-medium">₹{selectedOrder.total_amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Razorpay Order ID</span>
                    <span className="font-mono text-xs">{selectedOrder.razorpay_order_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Razorpay Payment ID</span>
                    <span className="font-mono text-xs">{selectedOrder.razorpay_payment_id}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="border rounded-lg overflow-hidden">
              <h3 className="font-medium text-gray-900 p-4 bg-gray-50 border-b">Order Items</h3>
              <div className="divide-y">
                {selectedOrder.items.map((item) => (
                  <div key={item.id} className="p-4 flex items-center gap-4">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{item.variant_details.product_name}</h4>
                      <div className="flex flex-wrap gap-4 mt-1 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <span>Color: {item.variant_details.color}</span>
                        </div>
                        <span>Size: {item.variant_details.size}</span>
                        <span>SKU: {item.variant_details.sku}</span>
                        <span>Qty: {item.quantity}</span>
                        <span>Price: ₹{parseFloat(item.price).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">₹{parseFloat(item.total).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-gray-50 border-t">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    {selectedOrder.shipping_fee && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shipping</span>
                        <span>₹{selectedOrder.shipping_fee}</span>
                      </div>
                    )}
                    {selectedOrder.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount</span>
                        <span>-₹{selectedOrder.discount}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium text-lg border-t pt-2">
                      <span>Total</span>
                      <span>₹{selectedOrder.total_amount}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Shipping Address</h3>
                <div className="space-y-1 text-sm">
                  <p className="font-medium">
                    {selectedOrder.shipping_address.first_name} {selectedOrder.shipping_address.last_name}
                  </p>
                  <p>{selectedOrder.shipping_address.street_address_1}</p>
                  {selectedOrder.shipping_address.street_address_2 && (
                    <p>{selectedOrder.shipping_address.street_address_2}</p>
                  )}
                  <p>
                    {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.pincode}
                  </p>
                  <p>{selectedOrder.shipping_address.country}</p>
                  <p>Phone: {selectedOrder.shipping_address.phone}</p>
                  <p>Email: {selectedOrder.shipping_address.email}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Billing Address</h3>
                <div className="space-y-1 text-sm">
                  <p className="font-medium">
                    {selectedOrder.billing_address.first_name} {selectedOrder.billing_address.last_name}
                  </p>
                  <p>{selectedOrder.billing_address.street_address_1}</p>
                  {selectedOrder.billing_address.street_address_2 && (
                    <p>{selectedOrder.billing_address.street_address_2}</p>
                  )}
                  <p>
                    {selectedOrder.billing_address.city}, {selectedOrder.billing_address.state} {selectedOrder.billing_address.pincode}
                  </p>
                  <p>{selectedOrder.billing_address.country}</p>
                  <p>Phone: {selectedOrder.billing_address.phone}</p>
                  <p>Email: {selectedOrder.billing_address.email}</p>
                </div>
              </div>
            </div>

            {/* Additional Notes or Comments */}
            {selectedOrder.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Order Notes</h3>
                <p className="text-sm text-gray-600">{selectedOrder.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AddressDetails = ({ address }) => {
  return (
    <div className="text-sm space-y-1">
      <p>{address.first_name} {address.last_name}</p>
      {address.company_name && <p>{address.company_name}</p>}
      <p>{address.street_address_1}</p>
      {address.street_address_2 && <p>{address.street_address_2}</p>}
      <p>{address.city}, {address.state} {address.pincode}</p>
      <p>{address.country}</p>
      <p>Phone: {address.phone}</p>
      <p>Email: {address.email}</p>
      {address.order_notes && <p>Notes: {address.order_notes}</p>}
    </div>
  );
};

const InvoiceModal = ({ invoice, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium">Invoice Preview</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 p-4 overflow-auto">
          {invoice.type.includes('pdf') ? (
            <iframe
              src={URL.createObjectURL(invoice)}
              className="w-full h-full"
              title="Invoice Preview"
            />
          ) : (
            <img
              src={URL.createObjectURL(invoice)}
              alt="Invoice"
              className="max-w-full h-auto mx-auto"
            />
          )}
        </div>
        <div className="p-4 border-t flex justify-end space-x-4">
          <a
            href={URL.createObjectURL(invoice)}
            download={`invoice-${Date.now()}.${invoice.type.split('/')[1]}`}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
          >
            <FiDownload className="w-4 h-4" />
            Download
          </a>
          <a
            href={URL.createObjectURL(invoice)}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
          >
            <FiExternalLink className="w-4 h-4" />
            Open in New Tab
          </a>
        </div>
      </div>
    </div>
  );
};

const InvoicePreview = ({ invoice, onClick }) => {
  if (!invoice) return null;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800"
    >
      <FiPaperclip className="w-4 h-4" />
      <span className="truncate max-w-[200px]">{invoice.name}</span>
    </button>
  );
};

export default Orders;



































